import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withAsyncCaller } from 'creatella-react-components/lib/HOCs/withAsyncCaller';
import NewLoginScreen from 'screens/NewLogin/NewLoginScreen';
import { connect } from 'react-redux';
import { handleLogin, loginError } from 'redux/reducers/auth';
import { notifType, notify } from 'utils/notifSender';
import { ROUTES, STORAGEKEY_AFTER_LOGOUT_MSG } from 'config/constants';
import OTPScreen from 'screens/Login/OTPScreen';
import NewDeviceScreen from 'screens/Login/NewDeviceScreen';
import { exchangeOtp, setApiRequestToken, requestNewOtp, resendAuthorizationEmail, unsetApiRequestToken } from 'api/auth';
import usePageDetails from 'hooks/usePageDetails';

NewLoginContainer.propTypes = {
    login: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    isAuthenticating: PropTypes.bool.isRequired,
    history: PropTypes.object.isRequired,
    apiCaller: PropTypes.func.isRequired
};

function NewLoginContainer({
    login,
    location,
    isAuthenticating,
    history,
    apiCaller
}) {
    const page = usePageDetails(apiCaller);
    const [form, setForm] = useState({
        email: '',
        password: ''
    });
    const [otpFormState, setOtpFormState] = useState({
        otp1: '',
        otp2: '',
        otp3: '',
        otp4: ''
    });
    const [otpCounter, setOtpCounter] = useState(60);
    const [startTimer, setStartTimer] = useState(false);
    const [loginStep, setLoginStep] = useState(0);
    const [userNumber, setUserNumber] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        const timer = () => setTimeout(() => {
            setOtpCounter(otpCounter - 1);
        }, 1000);

        if (otpCounter > 0 && startTimer) {
            timer();
        }

        if (otpCounter === 0 && startTimer) {
            setStartTimer(false);

            return () => clearTimeout(timer);
        }
    }, [otpCounter, startTimer]);

    useEffect(() => {
        const afterLogoutMessage = localStorage.getItem(
            STORAGEKEY_AFTER_LOGOUT_MSG
        );

        if (afterLogoutMessage) {
            notify(afterLogoutMessage, notifType.SUCCESS);
            localStorage.setItem(STORAGEKEY_AFTER_LOGOUT_MSG, '');
        }
    }, []);

    const handleForm = (e) => {
        setForm(e);
    };

    const setNewHeaderToken = async token => {
        setIsProcessing(true);

        try {
            await unsetApiRequestToken();
            await setApiRequestToken(token);

            setIsProcessing(false);

            return true;
        } catch (error) {
            if (error) {
                notify('An error occurred!', notifType.DANGER);
            }

            setIsProcessing(false);

            return false;
        }
    };

    const onLogin = async () => {
        const { state } = location;
        const redirectRoute = state && state.from && state.from.pathname;
        const redirectSearchQuery = redirectRoute && state.from.search;

        if (form.email === '') {
            notify('Email field is empty', notifType.DANGER);

            return true;
        }

        if (form.password === '') {
            notify('Password field is empty', notifType.DANGER);

            return true;
        }

        try {
            await login(form, redirectRoute, redirectSearchQuery);
        } catch (err) {
            if (err && err.response) {
                switch (Number(err.response.status)) {
                    case 428:
                        // eslint-disable-next-line no-case-declarations
                        const resp = await setNewHeaderToken(err.response.data.access_token);

                        if (resp) {
                            await setUserNumber(err.response.data.otp_sent_to);
                            setLoginStep(1);
                            setOtpCounter(60);
                            setStartTimer(true);
                        }

                        break;

                    default:
                        break;
                }
            } else {
                notify('An error occurred!', notifType.DANGER);
                loginError();
            }
        }
    };

    const submitLoginOtp = async ({ otp }) => {
        try {
            setIsProcessing(true);

            const { data } = await apiCaller(exchangeOtp, otp);
            const token = data.access_token;

            const resp = await setNewHeaderToken(token);

            if (resp) {
                setLoginStep(2);
            }
        } catch (error) {
            if (error?.response?.status === 422) {
                const keys = Object.keys(error.response.data.errors);

                keys.map(key =>
                    error.response.data.errors[key].map(msg => notify(msg, notifType.DANGER))
                );
            } else {
                notify('An error occurred!', notifType.DANGER);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const resendOtp = e => {
        if (e === 'login') {
            resendLoginOtp();
        }
    };

    const resendLoginOtp = async () => {
        try {
            setIsProcessing(true);

            await apiCaller(requestNewOtp);

            notify('New OTP code sent successfully!', notifType.SUCCESS);
            setOtpCounter(60);
            setStartTimer(true);
        } catch (error) {
            // 403 wait
            if (error?.response?.status === 403) {
                notify(error.response.data.message, notifType.DANGER);
            }

            notify('An error occurred!', notifType.DANGER);
        } finally {
            setIsProcessing(false);
        }
    };

    const resendEmail = async () => {
        try {
            setIsProcessing(true);

            await apiCaller(resendAuthorizationEmail);

            notify('New email sent successfully!', notifType.SUCCESS);
        } catch (error) {
            notify('An error occurred!', notifType.DANGER);
        } finally {
            setIsProcessing(false);
        }
    };

    switch (location.pathname) {
        case ROUTES.LOGIN:
            switch (loginStep) {
                case 0:
                    return (
                        <NewLoginScreen
                            apiCaller={apiCaller}
                            history={history}
                            isAuthenticating={isAuthenticating}
                            onLogin={onLogin}
                            handleForm={handleForm}
                            form={form} />
                    );

                case 1:
                    // error 428 happen
                    return (<OTPScreen
                        page={page}
                        userNumber={userNumber}
                        isProcessing={isProcessing}
                        onSubmit={submitLoginOtp}
                        formState={otpFormState}
                        setFormState={setOtpFormState}
                        counter={otpCounter}
                        content={<div>If you need to change your phone number, please contact your Middle Office admin.</div>}
                        resendOTP={() => resendOtp('login')} />);
                case 2:
                    return (<NewDeviceScreen page={page} resendEmail={resendEmail} isProcessing={isProcessing} />);

                default:
                    return (
                        <NewLoginScreen
                            apiCaller={apiCaller}
                            history={history}
                            isAuthenticating={isAuthenticating}
                            onLogin={onLogin}
                            handleForm={handleForm}
                            form={form} />
                    );
            }

        default:
            return null;
    }
}

function mapStateToProps({ auth }) {
    const { isAuthenticating } = auth;

    return {
        isAuthenticating
    };
}

function mapDispatchToProps(dispatch) {
    return {
        login: async (data, redirectRoute, redirectSearchQuery) => {
            // eslint-disable-next-line no-useless-catch
            try {
                const resp = await dispatch(handleLogin(data, redirectRoute, redirectSearchQuery));

                return resp;
            } catch (err) {
                throw err;
            }
        },
        loginError: () => dispatch(loginError())
    };
}

export default withAsyncCaller(connect(mapStateToProps, mapDispatchToProps)(NewLoginContainer));
